import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Flex,
  Grid,
  GridItem,
  Divider,
  Icon,
  Radio,
  RadioGroup,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Spinner,
  Tag,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter
} from '@chakra-ui/react';
import {
  BellIcon,
  PhoneIcon,
  EmailIcon,

  InfoOutlineIcon
} from '@chakra-ui/icons';
import { fetchWrapper } from '../../../../helpers';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {  } from 'react-icons/md';
import { LuGripVertical } from "react-icons/lu";
import { getUserTimeZone } from '../../../../utils/DefaultFunction';
import Select from 'react-select';
import {
  zonedTimeToUtc,
  validateZonedSchedule,
  nowInZone,
  formatInZone,
  zoneOffsetLabel,
  NO_TIMEZONE
} from '../../../../utils/timezoneSchedule';

/** "No/Tz" as the API spells the unresolved bucket reads as a raw code — show it as
 *  the same words the empty-state copy uses elsewhere. */
const timezoneDisplayLabel = (timezone) =>
  timezone === NO_TIMEZONE ? 'No Timezone' : timezone;

/**
 * react-select renders at its own default ~38px control height and 16px font — plain
 * next to the Date/Time inputs beside it, which are Chakra size="xs" (24px tall,
 * 12px font). Matched to those exact numbers so the row's controls line up instead
 * of the select sitting visibly taller.
 */
const compactZoneSelectStyles = {
  control: (base) => ({ ...base, minHeight: '24px', height: '24px', fontSize: '12px' }),
  valueContainer: (base) => ({ ...base, height: '24px', padding: '0 6px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: '24px' }),
  clearIndicator: (base) => ({ ...base, padding: '3px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  menu: (base) => ({ ...base, fontSize: '12px' }),
};

const getTimezones = () => {
  return Intl.supportedValuesOf('timeZone');
};


const CampaignReviewSubmit = ({
  formData,
  setFormData,
  onSubmitSuccess,
  isEditMode = false,
  campaignId = null,
  steps,

  currentStep,
  back
}) => {
  const [launchOption, setLaunchOption] = useState('immediate');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [costing, setCosting] = useState(null);
  const toast = useToast();
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [conversionEvents, setConversionEvents] = useState([]);
  const [selectedConversionEvent, setSelectedConversionEvent] = useState(null);
  const [senderIdOptions, setSenderIdOptions] = useState([]);
  const [selectedSenderId, setSelectedSenderId] = useState(null);
  const [emailSenderOptions, setEmailSenderOptions] = useState([]);
  const [selectedEmailSender, setSelectedEmailSender] = useState(null);
  // null = not fetched yet, true/false = outcome. Drives the "no senders" hint so
  // an empty list is not an unexplained dead end (launch is blocked without one).
  const [emailSendersLoaded, setEmailSendersLoaded] = useState(null);
  // Per-audience-timezone scheduling: one send time per timezone present in the
  // audience, each read as local wall-clock time in that zone.
  const [audienceTimezones, setAudienceTimezones] = useState([]);
  const [audienceTzLoading, setAudienceTzLoading] = useState(false);
  const [audienceTzError, setAudienceTzError] = useState('');
  // { 'Asia/Kolkata': { date: '2026-08-28', time: '12:00' } }
  const [tzSchedules, setTzSchedules] = useState({});
  // The "No Timezone" bucket has no real IANA zone of its own — this is the zone the
  // admin picks to schedule those contacts against. { value, label } from react-select,
  // or null while unassigned (falls back to sending the time exactly as entered).
  const [noTzTimezone, setNoTzTimezone] = useState(null);
  // zones whose row was edited by hand — they stop tracking the date/time above
  const tzEditedRef = React.useRef(new Set());
  const [isTzModalOpen, setIsTzModalOpen] = useState(false);
  // What to fall back to if the modal is cancelled: the radio that was selected
  // before, plus the schedule as it stood when the modal opened.
  const tzSnapshotRef = React.useRef(null);
  const prevChannelsRef = React.useRef([]);

  // Launch options are three radios: 'immediate', 'scheduled' (one instant for the
  // whole audience) and 'audience_timezones' (each timezone fires at its own local
  // time). The last two both post launch_type 'scheduled' — see prepareSubmissionData.
  const isPerZone = launchOption === 'audience_timezones';
  const isScheduled = launchOption === 'scheduled' || isPerZone;

  // A campaign saved as timezone_based 'N' stays single-timezone: editing it must not
  // turn it into a multi-timezone wave send, so that option is locked in edit mode.
  const lockedToSingleTimezone = isEditMode && formData?.launchSchedule?.timezoneBased !== true;
  // Get channel names for display
  const getChannelName = (channelId) => {
    const channelMap = {
      3: 'Email',
      1: 'SMS',
      6: 'Push Notifications',
      4: 'Voice (IVR)',
      5: 'In App Notification',

      2: 'WhatsApp'
    };
    return channelMap[channelId] || `Channel ${channelId}`;
  };

  // selectedChannels may hold channel IDs as numbers or strings depending on the source step —
  // compare loosely so "1" and 1 both match the SMS channel.
  const isSmsSelected = (channels) => (channels || []).some((c) => Number(c) === 1);

  // E-Mail is channel id 3 — same mapping as getChannelName above and /channel/list.
  const isEmailSelected = (channels) => (channels || []).some((c) => Number(c) === 3);

  const getChannelIcon = (channelId) => {
    const iconMap = {
      1: EmailIcon,
      2: PhoneIcon,
      4: BellIcon,
      5: PhoneIcon,
      6: PhoneIcon
    };
    return iconMap[channelId] || BellIcon;
  };

  const formatTimezonesForSelect = (timezonesArray) => {
    return timezonesArray.map((tz) => {
      // Get UTC offset
      const offset = new Date().toLocaleString('en-US', {
        timeZone: tz,
        timeZoneName: 'longOffset'
      }).split(' ').pop();

      return {
        value: tz,
        label: `${tz} (${offset})`,
        offset: offset.replace("GMT", "")
      };
    });
  };
  useEffect(() => {
    try {
      const allTimezones = getTimezones();
      const formattedOptions = formatTimezonesForSelect(allTimezones);
      setTimezoneOptions(formattedOptions);

      // Pre-fill with user's timezone
      const userTz = formData?.launchSchedule?.timezone;
      const userTzOption = formattedOptions.find(tz => tz.value === userTz);

      setSelectedTimezone(userTzOption || formattedOptions[0]);

    } catch (error) {
      console.error('Error loading timezones:', error);
      // Fallback timezone
      const fallbackOption = { value: 'UTC', label: 'UTC (UTC+0)' };
      setSelectedTimezone(fallbackOption);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  useEffect(() => {
    fetchWrapper.get(`${process.env.REACT_APP_API_URL}/event-conversations`)
      .then((res) => {
        const list = res?.data?.event_conversations || [];
        const options = list.map((e) => ({ value: e._id, label: e.event_name }));
        setConversionEvents(options);
        if (formData?.conversion_event) {
          const match = options.find((o) => o.value === formData.conversion_event);
          if (match) setSelectedConversionEvent(match);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SMS channel (id 1) uses a dedicated sender ID sent from a registered SMS account
  useEffect(() => {
    if (!isSmsSelected(formData.selectedChannels)) return;
    fetchWrapper.get(`${process.env.REACT_APP_API_URL}/channel/sms-list`)
      .then((res) => {
        const list = res?.data || [];
        const options = list.map((item) => ({ value: item.id, label: item.account_name }));
        setSenderIdOptions(options);
        if (formData?.sender_id) {
          const match = options.find((o) => o.value === formData.sender_id);
          if (match) setSelectedSenderId(match);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.selectedChannels]);

  // E-Mail channel (id 3) sends from a registered email account, chosen here
  useEffect(() => {
    if (!isEmailSelected(formData.selectedChannels)) return;
    fetchWrapper.get(`${process.env.REACT_APP_API_URL}/channel/email-list`)
      .then((res) => {
        const list = res?.data || [];
        const options = list.map((item) => ({
          value: item.id,
          // sms-list labels rows with account_name; fall back to the address fields
          // in case email accounts label themselves differently.
          label: item.account_name || item.from_email || item.email || item.name || String(item.id),
        }));
        setEmailSenderOptions(options);
        setEmailSendersLoaded(true);
        if (formData?.email_sender_id) {
          const match = options.find((o) => o.value === formData.email_sender_id);
          if (match) setSelectedEmailSender(match);
        }
      })
      .catch(() => {
        setEmailSenderOptions([]);
        setEmailSendersLoaded(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.selectedChannels]);

  useEffect(() => {
    if (!isEditMode) {
      try {
        const allTimezones = getTimezones();
        const formattedOptions = formatTimezonesForSelect(allTimezones);
        setTimezoneOptions(formattedOptions);

        // Pre-fill with user's timezone
        const userTz = getUserTimeZone()
        const userTzOption = formattedOptions.find(tz => tz.value === userTz);

        setSelectedTimezone(userTzOption || formattedOptions[0]);

      } catch (error) {
        console.error('Error loading timezones:', error);
        // Fallback timezone
        const fallbackOption = { value: 'UTC', label: 'UTC (UTC+0)' };
        setSelectedTimezone(fallbackOption);
      }
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * /campaigns/audience-timezones returns the timezones present in the audience.
   * Shape varies by build, so accept a bare list of zone names or a list of
   * objects carrying a count, under any of the usual wrappers.
   */
  const normalizeAudienceTimezones = (res) => {
    const raw =
      res?.data?.audience_timezones ||
      res?.data?.timezones ||
      res?.audience_timezones ||
      res?.timezones ||
      (Array.isArray(res?.data) ? res.data : null) ||
      (Array.isArray(res) ? res : []);

    const seen = new Set();
    return (raw || [])
      .map((item) => {
        if (typeof item === 'string') return { timezone: item, count: null };
        const timezone = item?.timezone || item?.time_zone || item?.tz || item?.name || item?.value;
        const count =
          item?.count ?? item?.total ?? item?.audience_count ?? item?.contacts ?? item?.subscribers ?? null;
        return timezone ? { timezone, count } : null;
      })
      .filter((item) => {
        // drop blanks/dupes and anything Intl cannot resolve, so a bad zone name
        // cannot crash the conversion below
        if (!item || seen.has(item.timezone)) return false;
        seen.add(item.timezone);
        try {
          nowInZone(item.timezone);
          return true;
        } catch (e) {
          return false;
        }
      })
      .sort((a, b) => (b.count || 0) - (a.count || 0) || a.timezone.localeCompare(b.timezone));
  };

  // Which slice of the audience this campaign targets — shared by the timezone
  // lookup and the campaign payload so the two can never disagree.
  const resolveAudienceType = () =>
    formData?.audienceType === 'all_subscribers'
      ? 'all_subscribers'
      : formData.sendToAll
        ? 'all_contacts'
        : formData.selectedCohort?._id
          ? 'cohort_contacts'
          : formData.selectedTags?.length > 0
            ? 'tagged_contacts'
            : null;

  const buildAudienceTimezonePayload = () => {
    const audience_type = resolveAudienceType();
    const payload = { audience_type };
    if (audience_type === 'tagged_contacts') {
      payload.target_tags = (formData.selectedTags || []).map((tag) => tag._id);
    }
    if (audience_type === 'cohort_contacts') {
      payload.target_cohort_id = formData.selectedCohort?._id || null;
    }
    return payload;
  };

  const fetchAudienceTimezones = async () => {
    setAudienceTzLoading(true);
    setAudienceTzError('');
    try {
      const res = await fetchWrapper.post(
        `${process.env.REACT_APP_API_URL}/campaigns/audience-timezones`,
        buildAudienceTimezonePayload()
      );
      const list = normalizeAudienceTimezones(res);
      setAudienceTimezones(list);
      if (!list.length) setAudienceTzError('No audience timezones returned for this campaign.');
      // rows are filled from the date/time above by the effect that watches them
    } catch (error) {
      console.error('Error loading audience timezones:', error);
      setAudienceTimezones([]);
      setAudienceTzError(error?.message || 'Could not load audience timezones. Please try again.');
    } finally {
      setAudienceTzLoading(false);
    }
  };

  // Fetch when the third radio is picked, and again when the audience changes
  // (tags/cohort/send-to-all) — a stale timezone list would schedule the wrong zones.
  const audienceSignature = JSON.stringify(buildAudienceTimezonePayload());
  useEffect(() => {
    if (!isPerZone) return;
    fetchAudienceTimezones();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceSignature, isPerZone]);

  const setTzRow = (timezone, patch) => {
    // Editing a row opts it out of following the date/time above.
    tzEditedRef.current.add(timezone);
    setTzSchedules((prev) => ({
      ...prev,
      [timezone]: { ...(prev[timezone] || { date: '', time: '' }), ...patch }
    }));
  };

  // Push the date/time picked above onto every zone, each read as that zone's local time.
  const applyBaseTimeToAllZones = () => {
    tzEditedRef.current.clear();
    setTzSchedules(
      audienceTimezones.reduce((acc, { timezone }) => {
        acc[timezone] = { date: scheduleDate || '', time: scheduleTime || '' };
        return acc;
      }, {})
    );
  };

  // Rows follow the date/time above until they are edited individually, so changing
  // the base time does not silently leave stale rows behind.
  useEffect(() => {
    if (!isPerZone || !audienceTimezones.length) return;
    setTzSchedules((prev) => {
      const next = { ...prev };
      audienceTimezones.forEach(({ timezone }) => {
        if (tzEditedRef.current.has(timezone)) return;
        next[timezone] = { date: scheduleDate || '', time: scheduleTime || '' };
      });
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleDate, scheduleTime, isPerZone, audienceTimezones]);

  // Substitute configured variables into any template string (text or HTML).
  const applyTemplateVariables = (input) => {
    let message = input || '';
    if (formData.template_variables) {
      Object.keys(formData.template_variables).forEach(variable => {
        const config = formData.template_variables[variable];
        let replacement = `[${variable}]`;

        if (config.type === 'fixed') {
          replacement = config.value;
        } else if (config.type === 'dynamic') {
          // Show the dynamic field name for preview
          const fieldName = config.value.replace('$', '');
          replacement = `[${fieldName}]`;
        }

        message = message.replace(new RegExp(`\\$${variable}`, 'g'), replacement);
      });
    }
    return message;
  };

  // Generate final message preview
  const generateFinalMessage = () => applyTemplateVariables(formData.selectedTemplate?.message);

  /**
   * ET_017: the preview rendered the template through <Text>, which escapes markup.
   * Fine for SMS, but an email template's body is HTML, so the campaign preview
   * showed raw <table>/<div> source instead of the designed email.
   *
   * Prefer an explicit email field; otherwise sniff for markup so a template whose
   * HTML arrives in `message` is still detected. Returns null for plain text, which
   * keeps the original <Text> path for SMS and the other channels.
   */
  const getEmailPreviewHtml = () => {
    const t = formData.selectedTemplate || {};
    const raw = t.html_code || t.emailHtml || t.email_code || t.email_html || '';
    if (raw && String(raw).trim()) return applyTemplateVariables(String(raw));
    const msg = formData.selectedTemplate?.message || '';
    return /<\/?(html|body|table|tr|td|div|p|br|span|img|a|h[1-6])\b/i.test(msg)
      ? applyTemplateVariables(msg)
      : null;
  };

  // Per-zone rows with their resolved instant and validity, for the table and the payload.
  //
  // The "No Timezone" bucket has no real IANA zone to compute an offset from — it is
  // scheduled against whichever zone the admin assigns via the standalone dropdown.
  // Silently defaulting to UTC when nothing is picked let this row through with a
  // guessed instant, so it is now blocked until a zone is actually assigned — the row
  // stays invalid even with a date and time filled in. Every other row's
  // resolvedTimezone is just its own timezone.
  const buildZoneSchedules = () =>
    audienceTimezones.map(({ timezone, count }) => {
      const row = tzSchedules[timezone] || { date: '', time: '' };
      const isNoTz = timezone === NO_TIMEZONE;
      if (isNoTz && !noTzTimezone) {
        return {
          timezone,
          resolvedTimezone: NO_TIMEZONE,
          count,
          date: row.date,
          time: row.time,
          valid: false,
          instant: null,
          reason: 'Assign a timezone before scheduling this group',
        };
      }
      const resolvedTimezone = isNoTz ? noTzTimezone.value : timezone;
      const check = validateZonedSchedule(row.date, row.time, resolvedTimezone);
      return { timezone, resolvedTimezone, count, date: row.date, time: row.time, ...check };
    });

  // Prepare submission data
  const prepareSubmissionData = () => {
    let scheduledAt = null;
    const perZone = isPerZone ? buildZoneSchedules() : [];

    if (isScheduled) {
      if (isPerZone) {
        // Earliest zone drives scheduled_at so the campaign's own start stays meaningful;
        // the per-zone instants below are what each segment should actually fire on.
        const instants = perZone.map((z) => z.instant).filter(Boolean);
        scheduledAt = instants.length
          ? new Date(Math.min(...instants.map((d) => d.getTime()))).toISOString()
          : null;
      } else {
        // Read date+time as wall-clock time in the chosen zone, resolved against the
        // scheduled date (not today) so DST transitions land correctly.
        const instant = zonedTimeToUtc(scheduleDate, scheduleTime, selectedTimezone?.value || getUserTimeZone());
        scheduledAt = instant ? instant.toISOString() : null;
      }
    }
    const audience_type = resolveAudienceType();


    return {
      name: formData.campaignName,
      description: formData.description,
      campaign_type: formData.campaignType == 3 ? "marketing" : "informational",
      template_id: formData.selectedTemplate?.id,
      audience_type: audience_type,
      target_tags: formData.sendToAll ? [] : formData.selectedTags.map(tag => tag._id),
      total_audience: formData.audienceCount,
      template_variables: formData.template_variables || {},
      selected_channels: formData.selectedChannels,
      // Per-timezone sends go out as timezone 'all' — each zone carries its own
      // local datetime in contact_timezone below.
      timezone: isPerZone ? 'all' : (selectedTimezone?.value || getUserTimeZone()),
      // Instead of launch_schedule, now using launch_type & scheduled_at
      launch_type: isScheduled ? 'scheduled' : 'immediate',
      scheduled_at: scheduledAt,
      timezone_based: isPerZone ? 'Y' : 'N',
      ...(isPerZone && {
        // the local time chosen once for every zone
        campaign_time: scheduleTime || null,
        // naive local wall-clock per zone, e.g. "2026-08-27T11:18:00" — no offset,
        // since the timezone field alongside it is what pins the instant
        contact_timezone: perZone.map((z) => ({
          timezone: z.resolvedTimezone,
          datetime: z.date && z.time ? `${z.date}T${z.time}:00` : null,
          // 1 = a real timezone resolved from the audience; 0 = the "No Timezone"
          // bucket, whether or not the admin assigned a zone to schedule it against.
          available_tz: z.timezone === NO_TIMEZONE ? 0 : 1,
        })),
      }),
      target_cohort_id: formData?.selectedCohort?._id ? formData?.selectedCohort?._id : null,
      channel_cost_summary: costing?.channel_cost_summary ?? null,
      conversion_event: selectedConversionEvent?.value || null,
      sender_id: isSmsSelected(formData.selectedChannels) ? (selectedSenderId?.value || null) : null,
      email_sender_id: isEmailSelected(formData.selectedChannels) ? (selectedEmailSender?.value || null) : null,
    };
  };




  // Submit campaign (create or update)
  const handleSubmit = async () => {


    if (formData.selectedChannels?.length === 0) {
      toast({
        title: `Warning`,
        description: `Please Select Channel`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (isSmsSelected(formData.selectedChannels) && !selectedSenderId?.value) {
      toast({
        title: `Warning`,
        description: `Please select a Sender ID for the SMS channel`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (isEmailSelected(formData.selectedChannels) && !selectedEmailSender?.value) {
      toast({
        title: `Warning`,
        description: `Please select a Sender for the E-Mail channel`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // A scheduled time that has already passed in its own zone would never fire.
    if (isScheduled) {
      if (isPerZone) {
        const zones = buildZoneSchedules();
        const bad = zones.filter((z) => !z.valid);
        if (!zones.length || bad.length) {
          toast({
            title: `Invalid schedule`,
            description: !zones.length
              ? `No audience timezones to schedule. Pick "Schedule for later" instead, or reload the timezones.`
              : `${bad.length} timezone${bad.length > 1 ? 's' : ''} need a valid future time: ${bad
                  .slice(0, 3)
                  .map((z) => z.timezone)
                  .join(', ')}${bad.length > 3 ? '…' : ''}`,
            status: "warning",
            duration: 6000,
            isClosable: true,
          });
          return;
        }
      } else {
        const check = validateZonedSchedule(scheduleDate, scheduleTime, selectedTimezone?.value || getUserTimeZone());
        if (!check.valid) {
          toast({
            title: `Invalid schedule`,
            description: `${check.reason} — pick a later time.`,
            status: "warning",
            duration: 6000,
            isClosable: true,
          });
          return;
        }
      }
    }

    try {
      setIsSubmitting(true);

      const submissionData = prepareSubmissionData();


      let url, method;
      if (isEditMode && campaignId) {
        // Update existing campaign
        url = `${process.env.REACT_APP_API_URL}/campaigns/${campaignId}`;
        method = 'put';
      } else {
        // Create new campaign
        url = `${process.env.REACT_APP_API_URL}/campaigns`;
        method = 'post';
      }

      const response = await fetchWrapper[method](url, submissionData);

      toast({
        title: `Campaign ${isEditMode ? 'Updated' : 'Created'} Successfully!`,
        description: `Your campaign has been ${isEditMode ? 'updated' : 'created'} and will be launched as scheduled.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Call success callback if provided
      if (onSubmitSuccess) {
        onSubmitSuccess(response);
      }

    } catch (error) {
      console.error('Campaign submission failed:', error);
      toast({
        title: `Campaign ${isEditMode ? 'Update' : 'Creation'} Failed`,
        description: error.message || `There was an error ${isEditMode ? 'updating' : 'creating'} your campaign. Please try again.`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Drop everything the per-timezone mode collected. Without this the rows survive
  // switching back to 'immediate'/'scheduled', and any later drift between the radio
  // and launchOption could still post contact_timezone rows with timezone_based 'Y'.
  const clearPerZoneState = () => {
    setAudienceTimezones([]);
    setTzSchedules({});
    setAudienceTzError('');
    setNoTzTimezone(null);
    tzEditedRef.current = new Set();
  };

  // Update formData when schedule options change
  const handleLaunchOptionChange = (value) => {
    // the radio is disabled in this case; refuse the switch anyway so no code path
    // can flip a single-timezone campaign to timezone_based 'Y' on update
    if (value === 'audience_timezones' && lockedToSingleTimezone) return;
    setLaunchOption(value);
    setFormData(prev => ({
      ...prev,
      launchSchedule: {
        type: value,
        ...(value !== 'immediate' && { date: scheduleDate, time: scheduleTime })
      }
    }));
    if (value === 'audience_timezones') {
      // Picking the per-timezone option is what opens the schedule modal.
      openTzModal(launchOption);
    } else {
      // Leaving the mode must leave no per-zone data behind — the payload is built
      // from these rows, so stale rows are what would send 'Y' by mistake.
      setIsTzModalOpen(false);
      clearPerZoneState();
    }
  };

  const openTzModal = (revertTo = launchOption) => {
    tzSnapshotRef.current = {
      // reopening from the summary button must not kick the user out of the mode on
      // cancel; only a fresh pick reverts to the previously selected radio
      launchOption: revertTo,
      scheduleDate,
      scheduleTime,
      tzSchedules,
      audienceTimezones,
      noTzTimezone,
      edited: new Set(tzEditedRef.current),
    };
    setIsTzModalOpen(true);
  };

  // Cancel restores the schedule exactly as it was when the modal opened, radio
  // included — so a cancelled first pick cannot leave the per-zone mode selected.
  const cancelTzModal = () => {
    const snap = tzSnapshotRef.current;
    if (snap) {
      setScheduleDate(snap.scheduleDate);
      setScheduleTime(snap.scheduleTime);
      setTzSchedules(snap.tzSchedules);
      setAudienceTimezones(snap.audienceTimezones);
      setNoTzTimezone(snap.noTzTimezone || null);
      tzEditedRef.current = new Set(snap.edited);
      setLaunchOption(snap.launchOption);
      if (snap.launchOption !== 'audience_timezones') clearPerZoneState();
    }
    setIsTzModalOpen(false);
  };

  const confirmTzModal = () => setIsTzModalOpen(false);

  // Guards against a stale pick: if the zone the admin assigned to "No Timezone"
  // shows up in the resolved audience list too (e.g. the audience changed), it would
  // now be scheduled twice under two rows — so drop the assignment and fall back to
  // "sent as entered" until a non-conflicting zone is chosen again.
  useEffect(() => {
    if (noTzTimezone && audienceTimezones.some((z) => z.timezone === noTzTimezone.value)) {
      setNoTzTimezone(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceTimezones]);

  useEffect(() => {
    if (isEditMode) {
      // A saved campaign only knows launch_type 'scheduled'; timezone_based === 'Y'
      // is what tells the two scheduled radios apart. The flag alone decides it —
      // the API keeps a single campaign_time and does not return the per-zone list,
      // so requiring contact_timezone here left the radio on "Schedule for later".
      const saved = formData.launchSchedule.contactTimezone || [];
      const savedPerZone = !!formData.launchSchedule.timezoneBased;
      setLaunchOption(savedPerZone ? 'audience_timezones' : formData.launchSchedule.type)
      setScheduleDate(formData.launchSchedule.date)
      // campaign_time comes back as "15:09:00"; <input type="time"> needs "15:09"
      const savedTime = String(formData.launchSchedule.campaignTime || '').slice(0, 5);
      setScheduleTime(savedTime || formData.launchSchedule.time)
      if (formData.launchSchedule.timezone && timezoneOptions.length > 0) {
        const tzOption = timezoneOptions.find(tz => tz.value === formData.launchSchedule.timezone);
        setSelectedTimezone(tzOption);
      }
      // If the API ever returns per-zone rows, restore them as hand-edited so their
      // saved times survive. Otherwise the zone list is refetched (the isPerZone
      // effect) and every row follows the saved date + campaign_time.
      if (savedPerZone && saved.length) {
        setAudienceTimezones(saved.map((s) => ({ timezone: s.timezone, count: s.audience_count ?? null })));
        setTzSchedules(
          saved.reduce((acc, s) => {
            // "2026-08-27T11:18:00" -> date + time
            const [datePart = '', timePart = ''] = String(s.datetime || '').split('T');
            acc[s.timezone] = { date: datePart, time: timePart.slice(0, 5) };
            return acc;
          }, {})
        );
        tzEditedRef.current = new Set(saved.map((s) => s.timezone));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.launchSchedule])



  useEffect(() => {
    const newChannels = formData.selectedChannels || [];
    const prevChannels = prevChannelsRef.current;

    const isLengthChanged = newChannels.length !== prevChannels.length;
    const isDifferentSet =
      newChannels.some(id => !prevChannels.includes(id)) ||
      prevChannels.some(id => !newChannels.includes(id));

    if ((isLengthChanged || isDifferentSet) && newChannels.length > 0) {
      updateCostEstimate(newChannels);
    }

    prevChannelsRef.current = [...newChannels];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.selectedChannels]);





  const { selectedChannels = [] } = formData;


  const updateCostEstimate = async (selectedChannels) => {
    try {
      setIsUpdating(true);

      const { sendToAll, selectedTags = [], selectedCohort = {}, country_id = 210 } = formData;

      let payload = {
        selected_channels: selectedChannels,
        country_id,
        campaign_type: formData?.campaignType === "3" ? "Marketing" : "Informational"
      };

      // 🧩 CASES
      if (sendToAll) {
        payload.audience_type = "all_contacts";
      } else if (selectedTags.length > 0) {
        payload.audience_type = "tagged_contacts";
        payload.target_tags = selectedTags.map(tag => tag._id);
      } else if (selectedCohort?._id) {
        payload.audience_type = "cohort_contacts";
        payload.target_cohort_id = selectedCohort._id;
      } else {
        console.warn("⚠️ No valid audience type found — skipping cost estimation.");
        setIsUpdating(false);
        return;
      }

      const url = `${process.env.REACT_APP_API_URL}/campaigns/cost-estimate`;
      const response = await fetchWrapper.post(url, payload);

      if (response.success) {
        setCosting(response.data);
      } else {
        console.error("Cost estimate failed:", response);
      }
    } catch (err) {
      console.error("Failed to call cost estimate API:", err);
    } finally {
      setIsUpdating(false);
    }
  };


  // ✅ handle reorder when drag ends
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = selectedChannels.indexOf(active.id);
    const newIndex = selectedChannels.indexOf(over.id);
    const newOrder = arrayMove(selectedChannels, oldIndex, newIndex);

    setFormData(prev => ({ ...prev, selectedChannels: newOrder }));

    // Call API for reorder
    await updateCostEstimate(newOrder);
  };

  const getDescription = (channelId) => {
    switch (channelId) {
      case 4:
        return "Instant mobile app notifications";
      case 5:
        return "Automated voice messages and calls";
      case 1:
        return "Rich content with detailed analytics";
      case 2:
        return "Direct SMS messaging";
      default:
        return "Messaging service";
    }
  };

  // ✅ Inner sortable item hook (kept inline)
  const SortableItem = ({ channelId }) => {
    const IconComponent = getChannelIcon(channelId);
    const name = getChannelName(channelId);
    const description = getDescription(channelId);

    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: channelId,
      disabled: isUpdating
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isUpdating ? 0.6 : 1,
    };

    return (
      <HStack
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        style={style}
        key={channelId}
        p={3}
        bg="#fff"
        borderRadius="md"
        border="1px solid"
        borderColor="#0000001a"
        cursor="grab"
        _hover={{ boxShadow: "0 1px 4px 0 #0000001a" }}
        align="center"
        spacing={3}
      >
        {/* 🟦 Drag Handle Icon */}
        <Icon as={LuGripVertical} color="#64748b" boxSize={5} />

        {/* 🟩 Channel Icon + Text */}
        <Box display="flex" alignItems="center" gap="10px">
          <Icon as={IconComponent} color="var(--brand-primary)" boxSize={4} />
          <Box>
            <Text fontWeight="medium">{name}</Text>
            <Text fontSize="xs" color="gray.600">
              {description}
            </Text>
          </Box>
        </Box>
      </HStack>
    );
  };


  if (!selectedChannels?.length) return null;
  const handleTimezoneChange = (option) => {
    setSelectedTimezone(option);
    setFormData(prev => ({
      ...prev,
      launchSchedule: {
        ...prev.launchSchedule,
        timezone: option?.value
      }
    }));
  };

  // "No Timezone" needs a decision (assign a zone, or accept the as-entered default)
  // that the other rows don't, so it always leads the table rather than falling
  // wherever its audience count happens to sort it.
  const zoneRows = isPerZone
    ? buildZoneSchedules().sort((a, b) => (a.timezone === NO_TIMEZONE ? -1 : b.timezone === NO_TIMEZONE ? 1 : 0))
    : [];
  const invalidZoneCount = zoneRows.filter((z) => !z.valid).length;
  // A zone already resolved in the audience (e.g. Asia/Karachi) cannot also be picked
  // for "No Timezone" — that would schedule the same zone under two rows.
  const audienceZoneValues = new Set(
    audienceTimezones.map((z) => z.timezone).filter((tz) => tz !== NO_TIMEZONE)
  );
  const noTzTimezoneOptions = timezoneOptions.filter((opt) => !audienceZoneValues.has(opt.value));
  // Only warn once the user has actually entered both parts.
  const singleZoneCheck =
    launchOption === 'scheduled' && scheduleDate && scheduleTime
      ? validateZonedSchedule(scheduleDate, scheduleTime, selectedTimezone?.value || getUserTimeZone())
      : null;
  const scheduleBlocked =
    isScheduled &&
    (isPerZone
      ? audienceTzLoading || !zoneRows.length || invalidZoneCount > 0
      : !scheduleDate || !scheduleTime || singleZoneCheck?.valid === false);

  return (
    <>


      <Box mt={4}>

        <Text fontSize="lg" fontWeight="semibold" mb={4}>
          Final Message Preview
        </Text>



        <Box
          bg={"#f1f5f980"}
          p={4}
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        // w={"50%"}
        >
          {getEmailPreviewHtml() ? (
            // Sandboxed iframe: renders the email as designed and keeps its CSS from
            // leaking into the app. sandbox="" blocks scripts in template content.
            <Box
              as="iframe"
              title="Email preview"
              srcDoc={getEmailPreviewHtml()}
              sandbox=""
              w="100%"
              h="520px"
              border="0"
              bg="white"
              borderRadius="md"
            />
          ) : (
            <Text fontSize="sm" whiteSpace="pre-wrap">
              {generateFinalMessage()}
            </Text>
          )}
        </Box>

        <Text fontSize="xs" color="#64748b" mt={2}>
          * Dynamic values will be replaced with actual customer data when sent
        </Text>

      </Box>



      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <GridItem>



          <Box border={"1px solid"} borderColor={"#0000001a"} p={4} borderRadius={"8.25px"} mt={4} bg={"#f1f5f980"}>
            <Flex justifyContent={"space-between"} alignItems={"center"}>
              <Text fontSize="lg" fontWeight="semibold" mb={4}>
                Delivery Channels
              </Text>
              <Text fontSize="xs" fontWeight="thin" mb={4}>
                Drag to reorder priority
              </Text>
            </Flex>
            {formData.selectedChannels?.length > 0 ? <VStack spacing={4} align="stretch">
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={selectedChannels}
                  strategy={verticalListSortingStrategy}
                >
                  <VStack spacing={4} align="stretch">
                    {selectedChannels.map((channelId) => (
                      <SortableItem key={channelId} channelId={channelId} />
                    ))}
                    <Text fontSize="xs" color="gray.500" mt={3}>
                      * If a channel fails, the message will automatically fall back to the next channel in order
                    </Text>
                  </VStack>
                </SortableContext>

              </DndContext>

            </VStack> : <Text fontSize="xs" fontWeight="light" >
              No channel selected
            </Text>}



          </Box>

        </GridItem>

        <GridItem>
          <Box
            border="1px solid"
            borderColor="var(--brand-tint-strong)"
            bg="var(--brand-tint)"
            borderRadius="lg"
            p={5}
            mt={4}
            w="full"
            maxW="100%"
          >
            {/* Header */}
            <HStack mb={3} spacing={2}>
              <Icon as={InfoOutlineIcon} color="var(--brand-primary)" boxSize={4} />
              <Text fontWeight="semibold" color="var(--brand-900)">
                Cost Estimate
              </Text>
            </HStack>

            {/* Items */}
            <VStack align="stretch" spacing={3} mb={3}>
              {costing?.items?.map((item, index) => (
                <Box key={item.channel_id}>

                  {/* CHANNEL ROW */}
                  <Flex align="center" justify="space-between" fontSize="sm">
                    <Flex align="center" gap={2}>
                      <Badge variant="outline" fontSize="xs">
                        #{index + 1}
                      </Badge>

                      <Text color="#64748b">
                        {getChannelName(+item.channel_id)}
                      </Text>
                    </Flex>

                    <Box textAlign="right">
                      <Text fontWeight="500">
                        {item?.currency_code} {(item?.total_cost_billing ?? item?.total_cost ?? 0).toLocaleString()}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formData?.campaignType === "3" ? "Marketing" : "Informational"}
                      </Text>
                    </Box>
                  </Flex>


                  {/* COUNTRY BREAKDOWN (LIKE YOUR EXAMPLE) */}
                  {item.pricing_mode === "by_country" && (
                    <VStack align="stretch" spacing={1.5} mt={2}>
                      {(item?.by_country || []).map((c, i) => (
                        <Flex key={i} justify="space-between" fontSize="xs">

                          {/* LEFT: contacts + country */}
                          <Text color="#64748b">
                            {c.quantity.toLocaleString()} {c.country_name}
                          </Text>

                          {/* RIGHT: price */}
                          <Text fontWeight="medium">
                            {item.currency_code} {c.subtotal_billing}
                          </Text>

                        </Flex>
                      ))}
                    </VStack>
                  )}
                </Box>
              ))}
            </VStack>

            <Divider my={4} />

            {/* Total */}
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontWeight="medium" color="var(--brand-900)">
                  Total Estimated Cost:
                </Text>
              </Box>
              <Box textAlign="right">

                <Text fontSize="lg" fontWeight="bold" color="var(--brand-900)">
                  {costing?.currency_code} {costing?.total_estimated_cost_billing?.toLocaleString()}
                </Text>
                <Text fontSize="sm" color="#64748b">
                  for {costing?.audience_count_total?.toLocaleString()} contacts
                </Text>
              </Box>
            </Flex>

            {/* Footer note */}
            <Text fontSize="xs" color="gray.500" mt={2}>
              * Actual cost may vary based on fallback usage and delivery success rates
            </Text>
          </Box>
        </GridItem>
      </Grid>

      <Divider mb={8} mt={10} />

      {/* Conversion Event */}
      <Box mb={6} w="40%">
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600" color="gray.600" mb={1.5}>
            Conversion Event <Text as="span" fontWeight="400" color="gray.400" fontSize="xs">(optional)</Text>
          </FormLabel>
          <Select
            options={conversionEvents}
            value={selectedConversionEvent}
            onChange={setSelectedConversionEvent}
            isClearable
            placeholder="Select a conversion event…"
            styles={{
              control: (base) => ({ ...base, fontSize: '14px', borderColor: '#e2e8f0', boxShadow: 'none', '&:hover': { borderColor: '#a0aec0' } }),
              option: (base, state) => ({ ...base, fontSize: '14px', background: state.isSelected ? 'var(--brand-primary)' : state.isFocused ? 'var(--brand-50)' : '#fff', color: state.isSelected ? '#fff' : '#1e293b' }),
            }}
          />
        </FormControl>
      </Box>
{console.log(formData)}
      {/* Sender ID — SMS channel only */}
      {isSmsSelected(formData.selectedChannels) && (
        <Box mb={6} w="40%">
          <FormControl isRequired>
            <FormLabel fontSize="sm" fontWeight="600" color="gray.600" mb={1.5}>
              Sender ID
            </FormLabel>
            <Select
              options={senderIdOptions}
              value={selectedSenderId}
              onChange={setSelectedSenderId}
              isClearable
              placeholder="Select a sender ID…"
              styles={{
                control: (base) => ({ ...base, fontSize: '14px', borderColor: '#e2e8f0', boxShadow: 'none', '&:hover': { borderColor: '#a0aec0' } }),
                option: (base, state) => ({ ...base, fontSize: '14px', background: state.isSelected ? 'var(--brand-primary)' : state.isFocused ? 'var(--brand-50)' : '#fff', color: state.isSelected ? '#fff' : '#1e293b' }),
              }}
            />
          </FormControl>
        </Box>
      )}

      {/* Email Sender — E-Mail channel only */}
      {isEmailSelected(formData.selectedChannels) && (
        <Box mb={6} w="40%">
          <FormControl isRequired>
            <FormLabel fontSize="sm" fontWeight="600" color="gray.600" mb={1.5}>
              Email Sender
            </FormLabel>
            <Select
              options={emailSenderOptions}
              value={selectedEmailSender}
              onChange={setSelectedEmailSender}
              isClearable
              placeholder="Select an email sender…"
              noOptionsMessage={() => 'No email senders configured'}
              styles={{
                control: (base) => ({ ...base, fontSize: '14px', borderColor: '#e2e8f0', boxShadow: 'none', '&:hover': { borderColor: '#a0aec0' } }),
                option: (base, state) => ({ ...base, fontSize: '14px', background: state.isSelected ? 'var(--brand-primary)' : state.isFocused ? 'var(--brand-50)' : '#fff', color: state.isSelected ? '#fff' : '#1e293b' }),
              }}
            />
            {emailSendersLoaded && emailSenderOptions.length === 0 && (
              <Text fontSize="xs" color="red.500" mt={1.5}>
                No email senders available. Add and verify an email account before launching an E-Mail campaign.
              </Text>
            )}
          </FormControl>
        </Box>
      )}

      <Box >
        <Text fontSize={"lg"} fontWeight={"semibold"} mb={4}>Review And Launch Campaign</Text>
        <Grid templateColumns="repeat(3, 1fr)" gap={4} w={"100%"}>


          <GridItem>
            <Box w="100%" border={"1px solid #0000001a"} borderRadius={"8.25px"} h={"100%"}>
              <Box p={6}>
                <Text fontSize="xl" fontWeight="semibold" mb={4}>
                  Campaign Summary
                </Text>

                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontWeight="medium">Name:</Text>
                    <Text>{formData.campaignName}</Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontWeight="medium">Type:</Text>
                    <Badge colorScheme="blue">
                      {formData.campaignType === '1' ? 'Transactional' :
                        formData.campaignType === '2' ? 'Promotional' :
                          formData.campaignType === '3' ? 'Marketing' :
                            formData.campaignType === '4' ? 'Informational' :
                              formData.campaignType || 'Other'}
                    </Badge>
                  </HStack>

                  <HStack justify="space-between" align="start">
                    <Text fontWeight="medium">Template:</Text>
                    <Text textAlign="right">{formData.selectedTemplate?.name}</Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontWeight="medium">Target Audience:</Text>
                    {!formData?.selectedCohort?._id && (<Text>{formData?.sendToAll ? formData?.allContactsTotal?.total : formData.taggedContactTotal?.total} contacts</Text>)}
                    {formData?.selectedCohort?._id && (<Text>{formData?.cohortsContact} contacts</Text>)}

                  </HStack>

                  <HStack justify="space-between" align="start">
                    <Text fontWeight="medium">Audience Type:</Text>
                    <Flex justify="flex-end" wrap="wrap" gap={2}>
                      {formData?.audienceType === 'all_subscribers' ? (
                        <Tag colorScheme="orange" borderRadius="full">
                          All Subscribers
                        </Tag>
                      ) : formData.sendToAll ? (
                        <Tag colorScheme="blue" borderRadius="full">
                          All Contacts
                        </Tag>
                      ) : formData.selectedTags?.length > 0 ? (
                        formData.selectedTags.map(tag => (
                          <Tag
                            key={tag._id || tag.name}
                            colorScheme="green"
                            borderRadius="full"
                          >
                            {tag.name}
                          </Tag>
                        ))
                      ) : formData.selectedCohort?._id ? (
                        <Tag colorScheme="purple" borderRadius="full">
                          Cohort Contacts
                        </Tag>
                      ) : (
                        <Tag colorScheme="gray" borderRadius="full">
                          No Audience Selected
                        </Tag>
                      )}
                    </Flex>
                  </HStack>


                </VStack>
              </Box>
            </Box>
          </GridItem>
          {/* Variable Configuration */}
          <GridItem>
            <Box border={"1px solid #0000001a"} borderRadius={"8.25px"} h={"100%"}>
              <Box p={6}>
                <Text fontSize="xl" fontWeight="semibold" mb={4}>
                  Variable Configuration
                </Text>

                {formData.template_variables && Object.keys(formData.template_variables).length > 0 ? (
                  <VStack spacing={3} align="stretch">
                    {Object.entries(formData.template_variables).map(([variable, config]) => (
                      <HStack key={variable} justify="space-between">
                        <Text fontWeight="medium">{variable}:</Text>
                        <Badge colorScheme={config.type === 'fixed' ? 'blue' : 'green'}>
                          {config.type === 'fixed' ? config.value : `[${config.value.replace('$', '')}]`}
                        </Badge>
                      </HStack>
                    ))}
                  </VStack>
                ) : (
                  <Text color="gray.500" fontSize="sm">No variables configured</Text>
                )}
              </Box>
            </Box>
          </GridItem>
          <GridItem>
            <Box border={"1px solid #0000001a"} borderRadius={"8.25px"} h={"100%"}>
              <Box p={6}>
                <Text fontSize="xl" fontWeight="semibold" mb={4}>
                  Launch Schedule
                </Text>

                <RadioGroup value={launchOption} onChange={handleLaunchOptionChange}>
                  <VStack spacing={4} align="stretch">
                    <Radio value="immediate">
                      <HStack>
                        {/* <Icon as={CheckCircleIcon} color="green.500" /> */}
                        <Text>Launch immediately</Text>
                      </HStack>
                    </Radio>

                    <Radio value="scheduled">
                      <HStack>
                        {/* <Icon as={CalendarIcon} color="blue.500" /> */}
                        <Text>Schedule for later</Text>
                      </HStack>
                    </Radio>

                    {/* Third option: one send time per timezone the audience is in.
                        Disabled when editing a campaign that was saved as single-timezone. */}
                    <Radio value="audience_timezones" alignItems="flex-start" isDisabled={lockedToSingleTimezone}>
                      <Box>
                        <Text>Schedule in each audience timezone</Text>
                        <Text fontSize="xs" color="gray.600">
                          {lockedToSingleTimezone
                            ? 'Not available here — this campaign was created to send at a single time. Create a new campaign to send per timezone.'
                            : 'Sends at the chosen local time in every timezone your audience is in, instead of one instant for everyone.'}
                        </Text>
                      </Box>
                    </Radio>

                    {/* Per-timezone times live in a modal — here we only summarise them */}
                    {isPerZone && (
                      <Box pt={1} pl={6}>
                        {zoneRows.length > 0 && invalidZoneCount === 0 ? (
                          <Text fontSize="xs" color="gray.700">
                            {zoneRows.length} timezone{zoneRows.length === 1 ? '' : 's'} scheduled
                            {scheduleTime ? ` at ${scheduleTime} local time` : ''}
                            {' · first send '}
                            {formatInZone(
                              new Date(Math.min(...zoneRows.map((z) => z.instant.getTime()))),
                              'UTC'
                            )} UTC
                          </Text>
                        ) : (
                          <Text fontSize="xs" color="red.500">
                            {audienceTzLoading
                              ? 'Loading audience timezones…'
                              : !zoneRows.length
                                ? 'No timezone schedule set yet'
                                : `${invalidZoneCount} timezone${invalidZoneCount > 1 ? 's' : ''} not ready to send`}
                          </Text>
                        )}
                        <Button size="xs" variant="outline" mt={2} onClick={() => openTzModal()}>
                          {zoneRows.length ? 'View / edit timezone schedule' : 'Set timezone schedule'}
                        </Button>
                      </Box>
                    )}

                    {launchOption === 'scheduled' && (
                      <Box pt={2} pl={6} maxW="640px">
                        <Grid templateColumns="minmax(0, 200px) minmax(0, 160px)" gap={3}>
                          <FormControl isRequired>
                            <FormLabel fontSize="sm" mb={1}>Date</FormLabel>
                            <Input
                              size="sm"
                              type="date"
                              value={scheduleDate}
                              onChange={(e) => setScheduleDate(e.target.value)}
                              min={new Date().toISOString().split('T')[0]}
                            />
                          </FormControl>
                          <FormControl isRequired>
                            <FormLabel fontSize="sm" mb={1}>Time</FormLabel>
                            <Input
                              size="sm"
                              type="time"
                              value={scheduleTime}
                              onChange={(e) => setScheduleTime(e.target.value)}
                            />
                          </FormControl>

                        </Grid>
                          <FormControl isRequired mt={3} maxW="368px">
                            <FormLabel fontSize="sm" mb={1}>Time Zone</FormLabel>
                            <Select
                              options={timezoneOptions}
                              value={selectedTimezone}
                              onChange={handleTimezoneChange}
                              placeholder="Select timezone"
                              isSearchable
                              isClearable={false}
                            // styles={customStyles}
                            />
                            {singleZoneCheck && (
                              <Text
                                fontSize="xs"
                                mt={1}
                                color={singleZoneCheck.valid ? 'gray.600' : 'red.500'}
                              >
                                {singleZoneCheck.valid
                                  ? `Sends ${formatInZone(singleZoneCheck.instant, selectedTimezone?.value || getUserTimeZone())} (${zoneOffsetLabel(selectedTimezone?.value || getUserTimeZone(), singleZoneCheck.instant)})`
                                  : singleZoneCheck.reason}
                              </Text>
                            )}
                          </FormControl>
                      </Box>
                    )}
                  </VStack>
                </RadioGroup>

                {/* Per-timezone schedule modal */}
                {/* 4xl (not 3xl): the "No Timezone" row's dropdown pushed the table's
                    content width past what 3xl left for it, which is what forced a
                    horizontal scrollbar on the table below. */}
                <Modal isOpen={isTzModalOpen} onClose={cancelTzModal} size="4xl" scrollBehavior="inside" isCentered>
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader fontSize="lg">Schedule in each audience timezone</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      <Grid templateColumns="minmax(0, 200px) minmax(0, 160px)" gap={3}>
                        <FormControl isRequired>
                          <FormLabel fontSize="sm" mb={1}>Date (local)</FormLabel>
                          <Input
                            size="sm"
                            type="date"
                            value={scheduleDate}
                            onChange={(e) => setScheduleDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                          />
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel fontSize="sm" mb={1}>Time (local)</FormLabel>
                          <Input
                            size="sm"
                            type="time"
                            value={scheduleTime}
                            onChange={(e) => setScheduleTime(e.target.value)}
                          />
                        </FormControl>
                      </Grid>

                          <Box mt={4}>
                            {audienceTzLoading ? (
                              <HStack fontSize="sm" color="gray.600">
                                <Spinner size="sm" />
                                <Text>Loading audience timezones…</Text>
                              </HStack>
                            ) : audienceTzError ? (
                              <HStack spacing={3} align="center">
                                <Text fontSize="xs" color="red.500">{audienceTzError}</Text>
                                <Button size="xs" variant="outline" onClick={fetchAudienceTimezones}>
                                  Retry
                                </Button>
                              </HStack>
                            ) : (
                              <>
                                <HStack justify="space-between" align="center" mb={1}>
                                  <Text fontSize="xs" color="gray.600">
                                    {zoneRows.length} timezone{zoneRows.length === 1 ? '' : 's'} in this audience —
                                    rows follow the time above until you edit them
                                  </Text>
                                  {zoneRows.length > 1 && (
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="gray"
                                      onClick={applyBaseTimeToAllZones}
                                      isDisabled={!scheduleDate || !scheduleTime}
                                    >
                                      Reset all
                                    </Button>
                                  )}
                                </HStack>

                                <TableContainer maxH="260px" overflowY="auto" overflowX="hidden" borderWidth="1px" borderRadius="md">
                                  <Table size="sm" variant="simple" sx={{ 'th, td': { px: 2, py: 1.5 } }}>
                                    <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                                      <Tr>
                                        <Th fontSize="10px" minW="250px">Timezone</Th>
                                        <Th fontSize="10px" isNumeric>Aud.</Th>
                                        <Th fontSize="10px">Date</Th>
                                        <Th fontSize="10px">Time</Th>
                                        <Th fontSize="10px">Sends</Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {zoneRows.map((z) => {
                                        const localNow = nowInZone(z.resolvedTimezone);
                                        const isNoTz = z.timezone === NO_TIMEZONE;
                                        return (
                                          <Tr
                                            key={z.timezone}
                                            bg={isNoTz ? 'orange.50' : undefined}
                                            sx={isNoTz ? { 'td:first-of-type': { borderLeft: '3px solid', borderLeftColor: 'orange.400' } } : undefined}
                                          >
                                            <Td minW="250px">
                                              <Text fontSize="xs" fontWeight="medium" noOfLines={1}>
                                                {timezoneDisplayLabel(z.timezone)}
                                              </Text>
                                              {isNoTz ? (
                                                <>
                                                  <Box mt={1} w="230px">
                                                    <Select
                                                      options={noTzTimezoneOptions}
                                                      value={noTzTimezone}
                                                      onChange={setNoTzTimezone}
                                                      placeholder="Assign a timezone…"
                                                      isClearable
                                                      isSearchable
                                                      menuPortalTarget={document.body}
                                                      components={{ IndicatorSeparator: () => null }}
                                                      styles={compactZoneSelectStyles}
                                                    />
                                                  </Box>
                                                  <Text fontSize="10px" color="gray.500" mt={1}>
                                                    {noTzTimezone
                                                      ? `${zoneOffsetLabel(noTzTimezone.value)} · now ${localNow.time}`
                                                      : 'No timezone on file — sent as entered (UTC) unless assigned'}
                                                  </Text>
                                                </>
                                              ) : (
                                                <Text fontSize="10px" color="gray.500">
                                                  {zoneOffsetLabel(z.timezone)} · now {localNow.time}
                                                </Text>
                                              )}
                                            </Td>
                                            <Td isNumeric fontSize="xs" color="gray.600">
                                              {z.count ?? '—'}
                                            </Td>
                                            <Td>
                                              <Input
                                                size="xs"
                                                w="130px"
                                                type="date"
                                                value={z.date}
                                                min={localNow.date}
                                                onChange={(e) => setTzRow(z.timezone, { date: e.target.value })}
                                                isInvalid={!z.valid}
                                              />
                                            </Td>
                                            <Td>
                                              <Input
                                                size="xs"
                                                w="100px"
                                                type="time"
                                                value={z.time}
                                                onChange={(e) => setTzRow(z.timezone, { time: e.target.value })}
                                                isInvalid={!z.valid}
                                              />
                                            </Td>
                                            <Td>
                                              {z.valid ? (
                                                <Text fontSize="10px" color="gray.600" whiteSpace="nowrap">
                                                  {formatInZone(z.instant, 'UTC')} UTC
                                                </Text>
                                              ) : (
                                                <Text fontSize="10px" color="red.500" whiteSpace="nowrap">
                                                  {isNoTz && !noTzTimezone
                                                    ? 'Assign a timezone'
                                                    : z.instant ? 'Already passed' : 'Set date & time'}
                                                </Text>
                                              )}
                                            </Td>
                                          </Tr>
                                        );
                                      })}
                                    </Tbody>
                                  </Table>
                                </TableContainer>

                                {invalidZoneCount > 0 && (
                                  <Text fontSize="xs" color="red.500" mt={2}>
                                    {invalidZoneCount === 1
                                      ? '1 row is not ready to send'
                                      : `${invalidZoneCount} rows are not ready to send`} — check the "Sends" column above for why.
                                  </Text>
                                )}
                              </>
                            )}
                          </Box>
                    </ModalBody>
                    <ModalFooter>
                      <Button size="sm" variant="ghost" mr={3} onClick={cancelTzModal}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        bg="var(--brand-primary)"
                        color="white"
                        _hover={{ bg: 'var(--brand-primary-active)' }}
                        _disabled={{ bg: 'gray.400' }}
                        onClick={confirmTzModal}
                        isDisabled={audienceTzLoading || !zoneRows.length || invalidZoneCount > 0}
                      >
                        Done
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>

                {!isEditMode && launchOption === 'scheduled' && (!scheduleDate || !scheduleTime) && (
                  <Text fontSize="xs" color="red.500" mt={2} textAlign="start">
                    Please select both date and time for scheduled launch
                  </Text>
                )}
              </Box>
            </Box>
          </GridItem>



        </Grid>
      </Box >


      <Flex justify="space-between" pt={4} >
        <Button onClick={back} variant="outline"
          size={"sm"}
          colorScheme="gray"

          px={8}>Previous</Button>

        <Button
          size="sm"
          // width="full"
          bg="var(--brand-primary)"
          color="white"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          loadingText={isEditMode ? "Updating Campaign..." : "Creating Campaign..."}
          _hover={{ bg: "var(--brand-primary-active)" }}
          _disabled={{ bg: "gray.400" }}
          disabled={
            (!isEditMode && scheduleBlocked)
            || formData.selectedChannels?.length <= 0
            // A missing sender is already rejected by handleSubmit; disabling here
            // makes the requirement visible instead of only failing on click.
            || (isSmsSelected(formData.selectedChannels) && !selectedSenderId?.value)
            || (isEmailSelected(formData.selectedChannels) && !selectedEmailSender?.value)
          }
        >
          {isSubmitting ? (
            <HStack>
              <Spinner size="sm" />
              <Text>{isEditMode ? "Updating Campaign..." : "Creating Campaign..."}</Text>
            </HStack>
          ) : (
            isEditMode ? 'Update Campaign' :
              // spell the mode out so the selected option is unmistakable before submit
              isPerZone
                ? `Schedule in ${zoneRows.length} timezone${zoneRows.length === 1 ? '' : 's'}`
                : `${launchOption === 'immediate' ? 'Launch' : 'Schedule'} Campaign`
          )}
        </Button>


      </Flex>
    </>
  );
};

export default CampaignReviewSubmit;